const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

export const OUTBOX_KEY = 'survey_outbox';

let flushing = false;

function readOutboxRaw() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

function writeOutbox(items) {
  localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
}

export function pendingCount() {
  return readOutboxRaw().length;
}

export function addToOutbox(payload) {
  const queue = readOutboxRaw();
  queue.push({
    ...payload,
    _queuedAt: `${new Date().toISOString()}_${Math.random().toString(36).slice(2, 8)}`,
  });
  writeOutbox(queue);
  return queue.length;
}

function isNetworkFailure(error) {
  if (!navigator.onLine) return true;
  const msg = String(error?.message || '').toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('network') || error?.name === 'TypeError';
}

function toApiPayload(row) {
  const { submitted_at: _at, ip_hash: _ip, surveyor, ...rest } = row;
  return {
    ...rest,
    referral_code: surveyor || row.referral_code || null,
  };
}

async function postSurveySubmit(row) {
  const response = await fetch(`${API_BASE}/api/survey/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toApiPayload(row)),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(result.error || 'Submit failed');
    err.code = result.code;
    err.httpStatus = response.status;
    throw err;
  }
  return {
    ok: true,
    duplicate: false,
    autoRejected: Boolean(result.autoRejected),
    fraudFlags: result.fraud_flags || [],
  };
}

export async function submitSurveyRow(row) {
  try {
    return await postSurveySubmit(row);
  } catch (error) {
    if (error.code === 'DUPLICATE_PHONE' || error.httpStatus === 409) {
      return { ok: false, duplicate: true, error };
    }
    if (error.httpStatus === 403) {
      return { ok: false, blockedReferral: true, error };
    }
    if (isNetworkFailure(error)) {
      throw new Error('offline');
    }
    throw error;
  }
}

export async function flushOutbox() {
  if (flushing) return { flushed: 0, remaining: pendingCount() };
  flushing = true;
  let flushed = 0;
  try {
    if (!navigator.onLine) return { flushed: 0, remaining: pendingCount() };
    const queue = readOutboxRaw();
    for (const item of queue) {
      const { _queuedAt, ...row } = item;
      try {
        const result = await postSurveySubmit(row);
        if (result.ok) {
          writeOutbox(readOutboxRaw().filter((q) => q._queuedAt !== item._queuedAt));
          flushed += 1;
        }
      } catch (err) {
        if (err.code === 'DUPLICATE_PHONE' || err.httpStatus === 409) {
          writeOutbox(readOutboxRaw().filter((q) => q._queuedAt !== item._queuedAt));
        }
      }
    }
  } finally {
    flushing = false;
  }
  return { flushed, remaining: pendingCount() };
}

export function classifySubmitError(error, t) {
  if (error?.code === 'DUPLICATE_PHONE' || error?.httpStatus === 409) {
    return t.errors.duplicateSurveyed || t.errors.duplicate;
  }
  if (error?.httpStatus === 403 || error?.blockedReferral) {
    return t.errors.blockedReferral;
  }
  const msg = String(error?.message || '').toLowerCase();
  if (msg.includes('rate_limit')) return t.errors.rateLimit;
  return t.errors.generic;
}
